import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, MapPin, MessageCircle, Send, CheckCircle2, Loader2, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { SITE, SERVICE_OPTIONS, BUDGET_OPTIONS } from "../../config/site";
import { api, formatApiError } from "../../lib/api";

const EMPTY = { full_name: "", org_name: "", email: "", phone: "", service: "", budget: "", details: "" };

const inputCls =
  "w-full rounded-lg border border-slate-200 bg-white px-4 py-3 font-inter text-[13.5px] text-navy placeholder:text-slate-400 transition-[border-color,box-shadow] duration-300 focus:border-gold focus:ring-2 focus:ring-gold/20";

const InfoRow = ({ icon: Icon, label, value, placeholder, testid }) => (
  <div className="flex items-center gap-4 rounded-lg border border-slate-200 bg-white px-5 py-4" data-testid={testid}>
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-mist text-gold ring-1 ring-slate-200">
      <Icon className="h-4.5 w-4.5" />
    </span>
    <div>
      <p className="font-mono2 text-[9.5px] uppercase tracking-[0.22em] text-slate-400">{label}</p>
      {value ? (
        <p className="mt-0.5 font-jakarta text-[14px] font-semibold text-navy">{value}</p>
      ) : (
        <p className="mt-0.5 inline-block rounded border border-dashed border-slate-300 px-2 py-0.5 font-mono2 text-[10.5px] tracking-wide text-slate-400">
          {placeholder}
        </p>
      )}
    </div>
  </div>
);

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle"); // idle | sending | sent
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    const onPrefill = (e) => setForm((f) => ({ ...f, service: e.detail }));
    window.addEventListener("rcs:prefill-service", onPrefill);
    return () => window.removeEventListener("rcs:prefill-service", onPrefill);
  }, []);

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.full_name.trim().length < 2) er.full_name = "Please enter your full name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email address";
    if (form.phone.trim().length < 7) er.phone = "Please enter a valid phone number";
    if (!form.service) er.service = "Please select a service";
    if (!form.budget) er.budget = "Please select a budget range";
    if (form.details.trim().length < 10) er.details = "Tell us a little more about your project (min 10 characters)";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;
    setState("sending");
    try {
      await api.post("/enquiries", form);
      setState("sent");
    } catch (err) {
      setServerError(formatApiError(err.response?.data?.detail));
      setState("idle");
    }
  };

  return (
    <section id="contact" className="bg-mist py-24 sm:py-32" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
          <Reveal>
            <Eyebrow>Contact</Eyebrow>
            <h2 className="mt-5 font-jakarta text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl" data-testid="contact-heading">
              Let's Talk About<br />Your Project
            </h2>
            <p className="mt-5 max-w-md font-inter text-[15px] leading-relaxed text-slate-500">
              Tell us what you want to build and our team will get back to you.
            </p>

            <div className="mt-9 space-y-3">
              <div className="rounded-lg border border-slate-200 bg-navy px-5 py-4" data-testid="contact-company-card">
                <p className="font-jakarta text-[15px] font-extrabold tracking-tight text-white">REBHAS CONSULTANCY SERVICES</p>
                <p className="mt-0.5 font-mono2 text-[10px] uppercase tracking-[0.28em] text-gold-light">RCS · Consulting. Technology. Growth.</p>
              </div>
              <InfoRow icon={Mail} label="Email" value={SITE.email} placeholder="[ADD OFFICIAL EMAIL]" testid="contact-email-info" />
              <InfoRow icon={Phone} label="Phone" value={SITE.phone} placeholder="[ADD OFFICIAL PHONE]" testid="contact-phone-info" />
              <InfoRow icon={MapPin} label="Location" value={SITE.location} placeholder="[ADD BUSINESS LOCATION]" testid="contact-location-info" />
              <InfoRow icon={MessageCircle} label="WhatsApp" value={SITE.whatsapp} placeholder="[ADD WHATSAPP NUMBER]" testid="contact-whatsapp-info" />
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="relative rounded-xl border border-slate-200 bg-white p-6 shadow-[0_30px_60px_-30px_rgba(17,24,39,0.18)] sm:p-9" data-testid="contact-form-card">
              <AnimatePresence mode="wait">
                {state === "sent" ? (
                  <motion.div
                    key="sent"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex min-h-[420px] flex-col items-center justify-center text-center"
                    data-testid="contact-form-success"
                  >
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gold-pale text-gold">
                      <CheckCircle2 className="h-8 w-8" />
                    </span>
                    <h3 className="mt-6 font-jakarta text-xl font-bold text-navy">Enquiry sent successfully</h3>
                    <p className="mt-2 max-w-sm font-inter text-[13.5px] leading-relaxed text-slate-500">
                      Thank you, {form.full_name.split(" ")[0]}. Your project enquiry for <strong className="text-navy">{form.service}</strong> has
                      been received. The RCS team will get back to you shortly.
                    </p>
                    <button
                      onClick={() => { setForm(EMPTY); setState("idle"); }}
                      data-testid="contact-send-another-button"
                      className="group mt-7 inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 font-jakarta text-[13px] font-semibold text-navy transition-colors duration-300 hover:border-gold hover:text-gold"
                    >
                      Send another enquiry
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                  </motion.div>
                ) : (
                  <motion.form key="form" exit={{ opacity: 0, y: -10 }} onSubmit={submit} noValidate data-testid="contact-enquiry-form">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="cf-name" className="mb-1.5 block font-jakarta text-[12px] font-semibold text-navy">Full Name *</label>
                        <input id="cf-name" data-testid="contact-name-input" className={inputCls} placeholder="Your name" value={form.full_name} onChange={set("full_name")} />
                        {errors.full_name && <p className="mt-1 text-[11px] text-red-500" data-testid="contact-name-error">{errors.full_name}</p>}
                      </div>
                      <div>
                        <label htmlFor="cf-org" className="mb-1.5 block font-jakarta text-[12px] font-semibold text-navy">Business / Organization Name</label>
                        <input id="cf-org" data-testid="contact-org-input" className={inputCls} placeholder="Company or school name" value={form.org_name} onChange={set("org_name")} />
                      </div>
                      <div>
                        <label htmlFor="cf-email" className="mb-1.5 block font-jakarta text-[12px] font-semibold text-navy">Email Address *</label>
                        <input id="cf-email" type="email" data-testid="contact-email-input" className={inputCls} placeholder="you@company.com" value={form.email} onChange={set("email")} />
                        {errors.email && <p className="mt-1 text-[11px] text-red-500" data-testid="contact-email-error">{errors.email}</p>}
                      </div>
                      <div>
                        <label htmlFor="cf-phone" className="mb-1.5 block font-jakarta text-[12px] font-semibold text-navy">Phone Number *</label>
                        <input id="cf-phone" type="tel" data-testid="contact-phone-input" className={inputCls} placeholder="+91 …" value={form.phone} onChange={set("phone")} />
                        {errors.phone && <p className="mt-1 text-[11px] text-red-500" data-testid="contact-phone-error">{errors.phone}</p>}
                      </div>
                      <div>
                        <label htmlFor="cf-service" className="mb-1.5 block font-jakarta text-[12px] font-semibold text-navy">Service Required *</label>
                        <select id="cf-service" data-testid="contact-service-select" className={inputCls} value={form.service} onChange={set("service")}>
                          <option value="">Select a service…</option>
                          {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        {errors.service && <p className="mt-1 text-[11px] text-red-500" data-testid="contact-service-error">{errors.service}</p>}
                      </div>
                      <div>
                        <label htmlFor="cf-budget" className="mb-1.5 block font-jakarta text-[12px] font-semibold text-navy">Estimated Budget *</label>
                        <select id="cf-budget" data-testid="contact-budget-select" className={inputCls} value={form.budget} onChange={set("budget")}>
                          <option value="">Select a range…</option>
                          {BUDGET_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                        {errors.budget && <p className="mt-1 text-[11px] text-red-500" data-testid="contact-budget-error">{errors.budget}</p>}
                      </div>
                      <div className="sm:col-span-2">
                        <label htmlFor="cf-details" className="mb-1.5 block font-jakarta text-[12px] font-semibold text-navy">Project Details *</label>
                        <textarea id="cf-details" rows={4} data-testid="contact-details-textarea" className={`${inputCls} resize-none`} placeholder="What do you want to build? Any specific features, timeline or references…" value={form.details} onChange={set("details")} />
                        {errors.details && <p className="mt-1 text-[11px] text-red-500" data-testid="contact-details-error">{errors.details}</p>}
                      </div>
                    </div>

                    {serverError && (
                      <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[12.5px] text-red-600" data-testid="contact-form-error">
                        {serverError}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={state === "sending"}
                      data-testid="contact-form-submit-button"
                      className="group mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-navy px-8 py-4 font-jakarta text-sm font-semibold text-white transition-colors duration-300 hover:bg-gold disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                    >
                      {state === "sending" ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Sending enquiry…
                        </>
                      ) : (
                        <>
                          Send Project Enquiry
                          <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </>
                      )}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
